"use client";

import { useVideoLoading } from "@/hooks/use-video-loading";
import { Play } from "lucide-react";

interface VideoBackgroundProps {
	src: string;
	poster: string;
	fallbackImage: string;
	className?: string;
	style?: React.CSSProperties;
	showLoadingIndicator?: boolean;
}

export default function VideoBackground({
	src,
	poster,
	fallbackImage,
	className = "",
	style = {},
	showLoadingIndicator = false,
}: VideoBackgroundProps) {
	const { videoProps, isLoaded, hasError, isAutoplayBlocked, retryPlay } = useVideoLoading({
		src,
		autoPlay: true,
		muted: true,
		loop: true,
		playsInline: true,
		preload: "auto",
	});

	return (
		<div className="absolute top-0 left-0 w-full h-full">
			{/* Background Image (always visible as fallback) */}
			<div
				className="absolute top-0 left-0 w-full h-full bg-cover bg-center bg-no-repeat transition-opacity duration-500"
				style={{
					backgroundImage: `url('${fallbackImage}')`,
					zIndex: 1,
					opacity: isLoaded && !hasError ? 0.3 : 1, // Fade out when video loads
				}}
			/>

			{/* Video Background */}
			{!hasError && (
				<video
					{...videoProps}
					poster={poster}
					className={`absolute top-0 left-0 w-full h-full object-cover ${className}`}
					aria-hidden="true"
					style={{
						zIndex: 2,
						...videoProps.style,
						...style,
					}}
				>
					<source src={src} type="video/mp4" />
					Your browser does not support the video tag.
				</video>
			)}

			{/* Loading indicator */}
			{showLoadingIndicator && !isLoaded && !hasError && (
				<div
					className="absolute top-0 left-0 w-full h-full flex items-center justify-center"
					style={{ zIndex: 3 }}
				>
					<div className="bg-black/30 backdrop-blur-sm rounded-lg p-3">
						<div className="flex items-center space-x-2 text-white">
							<div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
							<span className="text-xs">Chargement...</span>
						</div>
					</div>
				</div>
			)}

			{/* Manual fallback for strict autoplay policies */}
			{isAutoplayBlocked && !hasError && (
				<div
					className="absolute top-0 left-0 w-full h-full flex items-center justify-center"
					style={{ zIndex: 5 }}
				>
					<button
						type="button"
						onClick={() => void retryPlay()}
						className="inline-flex items-center gap-2 rounded-full bg-black/55 px-4 py-2 text-sm font-medium text-white backdrop-blur-sm transition hover:bg-black/70"
					>
						<Play className="h-4 w-4" />
						Lancer la video
					</button>
				</div>
			)}
		</div>
	);
}
